import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ArchiveRestore, ChevronDown, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** "clears in N days" chip — amber when a week or less is left, red on the last day. */
export const BinExpiry = ({ item, testId }) => {
  if (item?.days_left == null) return null;
  const d = item.days_left;
  const tone = d <= 1 ? 'bg-rose-50 text-rose-700 border-rose-200' : d <= 7 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-slate-50 text-slate-500 border-slate-200';
  const label = d <= 1 ? 'clears tomorrow' : `clears in ${d} days`;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[9px] ${tone}`} title={`Binned items are kept for 30 days, then cleared automatically${item.expires_at ? ` — on ${new Date(item.expires_at).toLocaleDateString()}` : ''}. Restore it before then to keep it.`} data-testid={testId}>
      {d <= 7 && <AlertTriangle className="w-2.5 h-2.5" />}{label}
    </span>
  );
};

/** Annexures deleted from this RFP — restore one exactly as it was, or remove it for good. */
export const DeletedAnnexures = ({ project, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const bin = project.deleted_documents || [];
  if (!bin.length) return null;
  const soon = bin.filter((d) => d.days_left <= 7).length;
  const base = `${API_URL}/api/rfp/response/projects/${project.id}/annexures`;
  const restore = async (d) => {
    setBusy(d.id);
    try {
      const r = await axios.post(`${base}/${d.id}/restore`, {}, { withCredentials: true });
      toast.success(`“${r.data.document.filename}” is back with its ${r.data.document.question_count} question${r.data.document.question_count === 1 ? '' : 's'}`);
      onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not restore'); }
    finally { setBusy(''); }
  };
  const purge = async (d) => {
    if (!window.confirm(`Permanently delete “${d.filename}” and its ${d.question_count} question${d.question_count === 1 ? '' : 's'}${d.answered ? ` (${d.answered} answered)` : ''}? This cannot be undone.`)) return;
    setBusy(d.id);
    try { await axios.delete(`${base}/${d.id}/permanent`, { withCredentials: true }); toast.success(`“${d.filename}” permanently deleted`); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete'); }
    finally { setBusy(''); }
  };
  return (
    <div className="border border-dashed border-slate-300 rounded-lg bg-slate-50/60 p-3" data-testid="rfp-resp-deleted-annexures">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 text-left" data-testid="rfp-resp-deleted-annexures-toggle">
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <Trash2 className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-xs font-semibold text-[#0F2040]">Deleted annexures</span>
        <span className="text-[10px] text-slate-500" data-testid="rfp-resp-deleted-annexures-count">{bin.length} in the bin — restore any that were deleted in error · kept 30 days</span>
        {soon > 0 && <span className="inline-flex items-center gap-1 text-[10px] text-amber-800" data-testid="rfp-resp-deleted-annexures-soon"><AlertTriangle className="w-3 h-3" />{soon} clear{soon === 1 ? 's' : ''} within a week</span>}
      </button>
      {open && (
        <ul className="mt-2 divide-y divide-slate-200" data-testid="rfp-resp-deleted-annexures-list">
          {bin.map((d) => (
            <li key={d.id} className="flex items-center gap-2 flex-wrap py-1.5 text-xs" data-testid={`rfp-resp-deleted-annexure-${d.id}`}>
              <span className="font-semibold text-slate-700 line-through decoration-slate-300">{d.filename}</span>
              <span className="text-slate-500">{d.question_count} question{d.question_count === 1 ? '' : 's'}{d.answered ? ` · ${d.answered} answered` : ''}</span>
              <span className="text-[10px] text-slate-400 ml-auto">deleted by {(d.deleted?.by || '').split('@')[0]}{d.deleted?.at ? ` · ${new Date(d.deleted.at).toLocaleString()}` : ''}</span>
              <BinExpiry item={d} testId={`rfp-resp-deleted-annexure-expiry-${d.id}`} />
              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={busy === d.id} onClick={() => restore(d)} data-testid={`rfp-resp-restore-annexure-${d.id}`}>
                {busy === d.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ArchiveRestore className="w-3 h-3 mr-1" />}Restore
              </Button>
              <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-slate-400 hover:text-red-600" disabled={busy === d.id} onClick={() => purge(d)} data-testid={`rfp-resp-purge-annexure-${d.id}`}>Delete for good</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
